import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { lighten, safeHex } from '@/lib/color'
import { applyDiscount, formatRupiah } from '@/lib/format'
import { Button, buttonStyles } from '@/components/generals/button'

import {
    defaultSize,
    packageArt,
    primaryCategory,
    sortedSizes,
    totalStock
} from '@/lib/product-utils'

import { useCart } from './cart-context'
import { StockBadge } from './stock-badge'
import { ProductStage } from './product-stage'

interface ProductCardProps {
    product: Product
    className?: string
}

export const ProductCard: FC<ProductCardProps> = ({ product, className }): ReactNode => {
    const cart = useCart()
    const [hovered, setHovered] = useState(false)
    const size = defaultSize(product)
    const sizes = sortedSizes(product)
    const stock = totalStock(product)
    const category = primaryCategory(product)
    const packageColor = safeHex(product.package_color, '#F7B32B')
    const price = size?.price ?? 0
    const finalPrice = applyDiscount(price, product.discount_percent)
    const soldOut = stock <= 0 || !size || size.stock <= 0

    const addToCart = () => {
        if (!size || soldOut) return

        cart.add({
            productId: product.id,
            slug: product.slug,
            name: product.name,
            sizeId: size.id,
            sizeLabel: size.label,
            variant: product.variants[0] ?? null,
            price: size.price,
            discountPercent: product.discount_percent,
            maxStock: size.stock,
            packageColor: product.package_color,
            accentColor: product.accent_color,
            image: product.image
        })
    }

    return (
        <article
            className={cn(
                'group relative flex h-full flex-col rounded-[28px] border-2 border-brand-choco/10 bg-white p-2.5 transition-all duration-300',
                'hover:-translate-y-1.5 hover:border-brand-choco hover:shadow-[0_10px_0_0_#3B1F0E]',
                className
            )}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) setHovered(false)
            }}
        >
            <Link
                href={`/produk/${product.slug}`}
                className='relative block aspect-[4/5] overflow-hidden rounded-[22px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40'
                style={{ background: `radial-gradient(circle at 50% 38%, ${lighten(packageColor, 0.72)} 0%, ${lighten(packageColor, 0.5)} 55%, ${lighten(packageColor, 0.3)} 100%)` }}
                aria-label={`Lihat detail ${product.name}`}
            >
                <div className='bg-sunburst absolute inset-0 opacity-60' aria-hidden='true' />

                <ProductStage art={packageArt(product)} hovered={hovered} cmsId={product.id} className='absolute inset-0' />

                <div className='pointer-events-none absolute left-3 top-3 flex flex-col items-start gap-1.5'>
                    {product.discount_percent > 0 && (
                        <span className='rounded-full border-2 border-brand-choco bg-brand-red px-2.5 py-0.5 font-heading text-sm font-bold text-white shadow-pop-sm'>
                            -{product.discount_percent}%
                        </span>
                    )}
                    {product.badge && (
                        <span className='rounded-full border-2 border-brand-choco bg-brand-yellow px-2.5 py-0.5 font-heading text-xs font-bold text-brand-choco'>
                            <span data-mdlfy-cms={`products:${product.id}:badge`}>{product.badge}</span>
                        </span>
                    )}
                </div>

                {soldOut && (
                    <div className='absolute inset-x-3 bottom-3 rounded-2xl bg-brand-choco/85 py-2 text-center font-heading text-sm font-semibold text-brand-cream'>
                        Stok sedang habis
                    </div>
                )}
            </Link>

            <div className='flex flex-1 flex-col px-2 pb-2 pt-4'>
                <div className='flex items-center justify-between gap-2'>
                    {category ? (
                        <span className='truncate text-xs font-semibold uppercase tracking-wider text-brand-orange'>
                            <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                        </span>
                    ) : <span />}
                    <StockBadge stock={stock} />
                </div>

                <h3 className='mt-2 font-heading text-xl font-bold leading-snug text-brand-choco'>
                    <Link href={`/produk/${product.slug}`} className='hover:text-brand-orange focus-visible:outline-none'>
                        <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                    </Link>
                </h3>

                <div className='relative mt-1.5 min-h-[44px] text-sm leading-relaxed'>
                    <p className={cn('line-clamp-2 text-muted-foreground transition-opacity duration-300', hovered && 'opacity-0')}>
                        {product.tagline && <span data-mdlfy-cms={`products:${product.id}:tagline`}>{product.tagline}</span>}
                    </p>
                    <p className={cn('absolute inset-0 line-clamp-2 font-medium text-brand-brown transition-opacity duration-300', hovered ? 'opacity-100' : 'opacity-0')} aria-hidden={!hovered}>
                        {product.flavor && (
                            <>
                                Rasa <span data-mdlfy-cms={`products:${product.id}:flavor`}>{product.flavor}</span>.{' '}
                            </>
                        )}
                        {product.variants.length > 1 ? `${product.variants.length} varian rasa, ` : ''}
                        {sizes.length} pilihan ukuran.
                    </p>
                </div>

                {sizes.length > 0 && (
                    <ul className='mt-3 flex flex-wrap gap-1.5' aria-label='Pilihan ukuran'>
                        {sizes.map(item => (
                            <li
                                key={item.id}
                                className={cn(
                                    'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                                    item.stock > 0 ? 'border-brand-choco/20 text-brand-choco' : 'border-dashed border-brand-choco/20 text-muted-foreground line-through'
                                )}
                            >
                                <span data-mdlfy-cms={`product_sizes:${item.id}:label`}>{item.label}</span>
                            </li>
                        ))}
                    </ul>
                )}

                <div className='mt-auto pt-4'>
                    <div className='flex items-end gap-2'>
                        <p className='font-heading text-2xl font-bold text-brand-choco'>
                            {sizes.length > 1 && <span className='mr-1 text-sm font-semibold text-muted-foreground'>mulai</span>}
                            {formatRupiah(finalPrice)}
                        </p>
                        {product.discount_percent > 0 && (
                            <p className='pb-1 text-sm text-muted-foreground line-through'>
                                {formatRupiah(price)}
                            </p>
                        )}
                    </div>

                    <div className='mt-3 grid gap-2'>
                        <Button
                            variant='primary'
                            size='md'
                            onClick={addToCart}
                            disabled={soldOut}
                            className='w-full px-3 text-sm'
                        >
                            <ShoppingBag className='size-4' aria-hidden='true' />
                            {soldOut ? 'Stok Habis' : 'Tambah ke Keranjang'}
                        </Button>
                        <Link
                            href={`/produk/${product.slug}`}
                            className={buttonStyles({ variant: 'outline', size: 'sm', className: 'w-full' })}
                        >
                            Lihat Detail
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                </div>
            </div>
        </article>
    )
}
